// <copyright file="SharedUtils.js">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import axios from "axios";
import type { Menu } from "primereact/menu";
import type { Toast } from "primereact/toast";
import type { SchemaVersion } from "src/components/DataLakeExplorer/DleRecordType";
import type { DleTableInfo } from "src/components/DataLakeExplorer/DleRecordTypeDetail";

/********************************************************
 * Shared utilities to be used across UI components
 ********************************************************/

// Data model for AWS sign-in url retrieved from AWS
export interface AwsSigninUrlInfo {
    SigninUrl: string;
}
const awsSignInDuration = 3600; // MUST match Lambda/ApiAwsConsoleUrl

/**
 * Status enumeration for external API calls and loading data
 */
export enum LoadingStatus {
    /* eslint-disable autofix/no-unused-vars */
    preload = "preload",
    loading = "loading",
    loaded = "loaded",
    error = "error",
    /* eslint-enable */
}

/**
 * User Permission Groups for Page Authorizations
 */
export enum Permission {
    /* eslint-disable autofix/no-unused-vars */
    ApiAdminReadWrite = "ApiAdminReadWrite",
    ApiCvdfRead = "ApiCvdfRead",
    UIAwsConsoleRead = "UIAwsConsoleRead",
    UILogin = "UILogin",
    UIQuickSightRead = "UIQuickSightRead",
    UIRsuCoverageRead = "UIRsuCoverageRead",
    UICvdfVisualizerRead = "UICvdfVisualizerRead",
    UIDataLakeExplorerRead = "UIDataLakeExplorerRead",
    UIEsuReportRead = "UIEsuReportRead"
    /* eslint-enable */
}

/**
 * Extract value of a request url parameter
 * @param param Target parameter
 * @param slicer Delimiter between url and parameters
 * @param delimiter Delimiter between parameters
 * @returns Value of parameter, null if not found
 */
export function getUrlParamValue({
    param,
    slicer,
    delimiter,
}: {
    param: string;
    slicer: string;
    delimiter: string;
}): string | null {
    console.debug(
        `getParameterValues window.location.href: ${window.location.href}`
    );
    let urlParms: string[] = [];
    if (window.location.href.includes(slicer)) {
        urlParms = window.location.href
            .slice(window.location.href.indexOf(slicer) + slicer.length)
            .split(delimiter);
    }

    for (const pp of urlParms) {
        const urlparm = pp.split("=");
        console.debug(
            `getParameterValues key=${urlparm[0]}, value=${urlparm[1].slice(
                0,
                50
            )}...`
        );
        if (urlparm[0].toLowerCase() === param && urlparm[1]) {
            return decodeURIComponent(urlparm[1]);
        }
    }

    return null;
}

/**
 * Extract value of a website cookie
 * @param cookieName Name of cookie
 * @returns Value of cookie, null if not found
 */
export function getCookie(cookieName: string) {
    const temp = document.cookie.match(
        "(^|;) ?" + cookieName + "=([^;]*)(;|$)"
    );
    return temp ? temp[2] : null;
}

/**
 * Extract value of a website cookie containing JSON value
 * @param cookieName Name of cookie
 * @param fieldName Name of field within JSON value
 * @returns Value of cookie field, null if cookie or field not found
 */
export function getCookieJsonField(
    cookieName: string,
    fieldName: string
): string | null {
    const temp = document.cookie.match(
        "(^|;) ?" + cookieName + "=([^;]*)(;|$)"
    );
    const cookie = temp ? temp[2] : null;
    if (cookie) {
        const cookieJson = JSON.parse(cookie);
        if (cookieJson[fieldName]) {
            return cookieJson[fieldName];
        }
    }

    return null;
}

/**
 * Function which searches config featureflags for desired feature
 * @param searchString Config FeatureFlag feature to search for
 * @param featureFlags Config featureFlags section
 * @param defaultCase default to return if section does not exist
 * @returns boolean enabled/disabled value of the desired config feature or defaultCase if not defined
 */
export function checkConfigFeatureFlag(
    searchString: string,
    featureFlags: { name: string; enabled: boolean }[],
    defaultCase = false
) {
    let featConfig = defaultCase;
    const configProp = featureFlags.filter((o) => o.name == searchString);
    if (configProp.length > 0) {
        featConfig = configProp[0].enabled;
    }
    return featConfig;
}

/**
 * Function which searches config featureflags for desired section
 * @param searchString Config FeatureFlag section to search for
 * @param featureFlags Config featureFlags section
 * @param defaultCase default to return if section does not exist
 * @returns boolean enabled/disabled value of the desired config section or defaultCase if not defined
 */
export function checkConfigSectionFlag(
    searchString: string,
    featureFlags: { name: string; enabled: boolean }[]
) {
    const configProp = featureFlags.filter((o) =>
        o.name.includes(searchString)
    );
    return configProp.some((element) => element.enabled);
}

/**
 * Check that Id Token contains appropriate user group permission for element
 * @param permissionGroup Name of permission group to check
 * @returns boolean true if user is part of group false otherwise
 */
export function checkUserPermission(permissionGroup: Permission): boolean {
    try {
        const idToken = getCookieJsonField("DepSessionIdentity", "token");
        if (!idToken) {
            console.debug("IdToken Does Not Exist");
            return false;
        }
        // Pad the idtoken and parse to json
        const idTokStr = idToken.split(".")[1];
        const idTokStrPadded = idTokStr.padEnd(
            idTokStr.length +
                (idTokStr.length % 4 === 0 ? 0 : 4 - (idTokStr.length % 4)),
            "="
        );
        const idTokPayload = JSON.parse(atob(idTokStrPadded));

        if (!idTokPayload.permission) {
            console.debug("No permissions defined for user");
            return false;
        }

        console.debug(`permissions: ${idTokPayload["permission"]}`);

        if (idTokPayload["permission"].includes(permissionGroup)) {
            return true;
        } else {
            console.debug("Invalid user permissions");
            return false;
        }
    } catch {
        console.error("Failed to validate User Permissions");
        return false;
    }
}

/**
 * Function which searches config featureflags for desired feature and if user has permission in token
 * @param searchString Config FeatureFlag feature to search for
 * @param featureFlags Config featureFlags section
 * @param permissionGroup Name of permission group to check
 * @param defaultCase default to return if section does not exist
 * @returns boolean enabled/disabled value of the desired config feature or defaultCase if not defined
 */
export function checkFeatFlagandPermissions(
    searchString: string,
    featureFlags: { name: string; enabled: boolean }[],
    permissionGroup: Permission,
    defaultCase = false
) {
    return (
        checkConfigFeatureFlag(searchString, featureFlags, defaultCase) &&
        checkUserPermission(permissionGroup)
    );
}

/**
 * Function which checks if the user has any Dashboard permissions
 * @returns true if user has 1 or more permissions for a dashboard view
 */
export function checkDashboardPermissions() {
    const dashboardPermissions = [
        Permission.UIQuickSightRead,
        Permission.UIRsuCoverageRead,
        Permission.UICvdfVisualizerRead,
        Permission.UIEsuReportRead
    ];
    if (dashboardPermissions.some((element) => checkUserPermission(element))) {
        return true;
    } else return false;
}

/**
 * Set keyboard input focus on first menu item. This is intended
 * for use when a menu is opened/shown for accessibility purposes.
 * @param menuRef Menu reference object
 */
export function menuFocus(menuRef: React.RefObject<Menu>) {
    // HTMLElement.focus() sets keyboard input focus on an element, IFF it can be focused.
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus

    // Elements with tabindex attribute can be focused, such as generated from a primereact MenuItem.
    // https://blog.whatwg.org/focusing-on-focus

    // Wait a tick for menu items to render, then focus on first item
    setTimeout(() => {
        // Use a classname primereact automatically assigns to tabindex menu items to find element to focus.
        // You can see this by looking at the rendered DOM for menu items.
        const firstItem = menuRef.current
            ?.getElement()
            ?.getElementsByClassName("p-menuitem-link")
            .item(0);
        (firstItem as HTMLElement)?.focus();
    }, 1);
}

/**
 * Handles errors loading or displaying data; sets error message element and triggers toast alert
 * @param summary Error summary title
 * @param detail Error message detail
 * @param toastCurrent Instance of a toast (popup message) component
 * @param setLoadingStatus Function that updates state of loading status
 */
export function displayLoadingError(
    summary: string,
    detail: string,
    toastCurrent: Toast | null,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>> | null
) {
    detail = JSON.stringify(detail);
    const errorDiv =
        document?.getElementsByClassName("App-content-error") || [];
    if (errorDiv.length > 0) {
        errorDiv[0].textContent = `${summary}: ${detail}`;
    }
    toastCurrent?.show({
        severity: "error",
        summary: summary,
        detail: detail,
        sticky: true, // Show until dismissed by user
    });
    setLoadingStatus && setLoadingStatus(LoadingStatus.error);
}

/**
 * Create a link to an AWS web console endpoint with federated sign-in credentials
 *
 * @param apiBaseUrl Base URL for the user interface API
 * @param destination AWS web console endpoint, e.g. https://us-east-1.console.aws.amazon.com/athena/home/query-editor
 * @param toastCurrent Optional instance of a toast (popup message) component, used for error/info messages
 * @param setLoadingStatus Optional function that sets loading status, used by loading spinners
 *
 * @returns AWS web console link with federated sign-in credentials
 */
export async function getAwsFederatedSigninUrl(
    apiBaseUrl: string,
    destination: string,
    toastCurrent: Toast | null,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>> | null
): Promise<string> {
    // Fetch new sign-in token and resolve URL
    console.debug("Fetching AWS sign-in url...");
    setLoadingStatus && setLoadingStatus(LoadingStatus.loading);
    const idToken = getCookieJsonField("DepSessionIdentity", "token");
    const headers = {
        headers: {
            // Use the OID identity (not access) token issed by Cognito after login
            Authorization: `Bearer ${idToken}`,
        },
    };
    return await new Promise((resolve, reject) => {
        // Retrieve AWS web console home url, then modify for desired destination
        const errorSummary = "Error fetching AWS sign-in url";
        let errorDetail = "unknown";
        axios
            .get<AwsSigninUrlInfo>(`${apiBaseUrl}/aws-console`, headers)
            .then((response) => {
                const signInUrl = response.data.SigninUrl;
                const signInToken = signInUrl.substring(
                    signInUrl.lastIndexOf("=") + 1
                );

                // Store AWS session info in cookie
                const cookieDuration = awsSignInDuration - 300; // Sign-in token duration seconds minus 5 min
                const epochTimeNow = Math.floor(Date.now() / 1000);
                const expiryTs = epochTimeNow + cookieDuration;
                const awsSessionInfo = `{ "signInToken": "${signInToken}", "expiryTs": ${expiryTs} }`;
                document.cookie = `awsSessionInfo=${awsSessionInfo}; Max-Age=${cookieDuration}; SameSite=Strict`;

                console.debug(
                    `Resolved sign-in token from API: ${signInToken}`
                );
                console.debug(`Resolved sign-in expiry from API: ${expiryTs}`);
                setLoadingStatus && setLoadingStatus(LoadingStatus.loaded);
                resolve(
                    `https://signin.aws.amazon.com/federation?Action=login&Issuer=v2xdep&Destination=${encodeURIComponent(
                        destination
                    )}&SigninToken=${signInToken}`
                );
            })
            .catch((error) => {
                errorDetail = error.response
                    ? error.response.data.errorMessage
                    : error;
                console.debug(`${errorSummary}: ${errorDetail}`);
                displayLoadingError(
                    errorSummary,
                    errorDetail,
                    toastCurrent,
                    setLoadingStatus
                );
                document.cookie = `awsSessionInfo=; SameSite=Strict`; // Clear AWS session info cookie
                reject({ summary: errorSummary, detail: errorDetail });
            });
    });
}

export function sortDleSchemaVersions(aa: SchemaVersion, bb: SchemaVersion) {
    // Sort by table prefix, then schema version with most recent first
    const tableNameSort = sortDleTableNames({tableName: aa.TableName} as DleTableInfo, {tableName: bb.TableName } as DleTableInfo);
    if (tableNameSort != 0) {
        return tableNameSort;
    }
    const aaVersionNum = parseInt(aa.MetadataVersion.split("v")[1]);
    const bbVersionNum = parseInt(bb.MetadataVersion.split("v")[1]);
    return bbVersionNum - aaVersionNum; // Most recent first (desc)
}

export function sortDleTableNames(aa: DleTableInfo, bb: DleTableInfo) {
    // Sort by table prefix
    const aaPrefix = aa.tableName.split("_")[0];
    const bbPrefix = bb.tableName.split("_")[0];
    if (aaPrefix < bbPrefix) {
        return 1;
    }
    if (aaPrefix > bbPrefix) {
        return -1;
    }
    return 0;
}
